/**
 * Authentication Context Provider
 *
 * This module provides authentication state management for the entire application.
 * It handles user login, registration, logout, session management, and onboarding status.
 *
 * The context exposes the following functionality:
 * - Current user and session information
 * - Authentication status and loading state
 * - Login, register, and logout functions
 * - Session refresh and validation
 * - Onboarding status checking
 * - Backend user ID for API integrations
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  ORGANIZATION_KEYS,
  USER_KEYS,
  SUBSCRIPTION_KEYS,
  BILLING_ENFORCEMENT_KEYS,
} from '../constants/queryKeys';
import authService, { UserInfo } from '../services/authService';
import navigationService from '../services/navigationService';
import onboardingService, { OnboardingStatus } from '../services/onboardingService';
import { shouldSuppressRedirect } from '../utils/navigationRedirectHelpers';
import { logError } from '../utils/errors';
import { uiLogger } from '../utils/loggerInstance';
import { NangoService } from '../services/integrations/nangoService';
import {
  setAnalyticsUserId,
  setAnalyticsUserProperties,
  suppressAnalyticsForEmail,
  isEmailExcludedFromAnalytics,
} from '../services/firebaseAnalytics';
import { ENV } from '../config/env';
import { analyticsEvents } from '../utils/analyticsEvents';
import { handleAuthError as handleSharedAuthError } from '../services/authErrorHelper';
import { useChatStore } from '../stores/chatStore';
import { clearDatadogUser, setDatadogUser } from '../monitoring/datadogClient';

// Constants
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Authentication Context Type Definition
 * Defines the shape of the context data and functions
 */
interface AuthContextType {
  /** Current authenticated user or null if not authenticated */
  user: UserInfo['user'] | null;

  /** Current session data or null if not authenticated */
  session: UserInfo['session'] | null;

  /** Backend user ID for API integrations */
  backendUserId: string | null;

  /** Whether authentication state is being loaded */
  loading: boolean;

  /** Whether JWT verification/refresh has completed and auth state is ready */
  authReady: boolean;

  /** Login function - authenticates user with email and password */
  login: (email: string, password: string) => Promise<void>;

  /** Verify MFA code and complete login */
  verifyMFAAndLogin: (mfaToken: string, code: string, backupCode?: string) => Promise<void>;

  /** Login with Google OAuth function */
  loginWithGoogle: (verificationCode?: string) => Promise<void>;

  /** Login with Microsoft OAuth function */
  loginWithMicrosoft: (verificationCode?: string) => Promise<void>;

  /** Login with Okta SSO function */
  loginWithOkta: () => Promise<void>;

  /** Login with company SSO (Okta or Microsoft) based on identifier */
  loginWithCompanySSO: (companyIdentifier: string) => Promise<void>;

  /** Register function - creates a new user account */
  register: (
    email: string,
    password?: string,
    verificationCode?: string,
    firstName?: string,
    lastName?: string,
    invitationToken?: string,
    termsAccepted?: boolean,
    marketingEmailsOptIn?: boolean,
  ) => Promise<void>;

  /** Logout function - ends the current session */
  logout: () => Promise<void>;

  /** Refreshes authentication state without triggering redirects */
  refreshAuth: () => Promise<void>;

  /** Checks if the user has completed onboarding */
  checkOnboardingStatus: () => Promise<OnboardingStatus | null>;

  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;

  /** Get the backend user ID for integrations */
  getBackendUserId: () => string | null;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  backendUserId: null,
  loading: true,
  authReady: false,
  login: async () => {},
  verifyMFAAndLogin: async () => {},
  loginWithGoogle: async () => {},
  loginWithMicrosoft: async () => {},
  loginWithOkta: async () => {},
  loginWithCompanySSO: async () => {},
  register: async () => {},
  logout: async () => {},
  refreshAuth: async () => {},
  checkOnboardingStatus: async () => null,
  isAuthenticated: false,
  getBackendUserId: () => null,
});

/**
 * Custom hook to use the auth context
 * @returns The auth context value
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Manages authentication state and provides authentication functions
 * to the entire application through React Context.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserInfo['user'] | null>(null);
  const [session, setSession] = useState<UserInfo['session'] | null>(null);
  const [backendUserId, setBackendUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // Initialize QueryClient to clear cache on logout
  const queryClient = useQueryClient();

  // Track previous user so we only invalidate when user identity changes (e.g. after login)
  const prevBackendUserIdRef = useRef<string | null>(null);

  // Initialize NangoService for integration management
  const nangoService = NangoService.getInstance();

  // When the logged-in user changes (e.g. logout then login as different user), invalidate
  // user-scoped caches so the new user always gets fresh org/profile/billing data.
  useEffect(() => {
    if (backendUserId && prevBackendUserIdRef.current !== backendUserId) {
      prevBackendUserIdRef.current = backendUserId;
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: USER_KEYS.profile() });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
      queryClient.invalidateQueries({ queryKey: BILLING_ENFORCEMENT_KEYS.all });
      uiLogger.debug('Invalidated user-scoped caches for new user', { backendUserId });
    } else if (!backendUserId) {
      prevBackendUserIdRef.current = null;
    }
  }, [backendUserId, queryClient]);

  // Keep analytics suppression in sync with the current user (also covers session restore flows).
  useEffect(() => {
    suppressAnalyticsForEmail(user?.email);
  }, [user?.email]);

  // Keep analytics User ID in sync even when backendUserId is hydrated later (session restore / refresh).
  const lastAnalyticsUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!backendUserId) {
      lastAnalyticsUserIdRef.current = null;
      return;
    }
    if (lastAnalyticsUserIdRef.current === backendUserId) return;
    lastAnalyticsUserIdRef.current = backendUserId;
    void setAnalyticsUserId(backendUserId);
  }, [backendUserId]);

  /**
   * Update backend user ID from authService's internal state
   */
  const updateBackendUserId = useCallback(() => {
    const internalUserId = authService.getBackendUserId();
    if (internalUserId && internalUserId !== backendUserId) {
      uiLogger.debug('Updating backend user ID', { internalUserId });
      setBackendUserId(internalUserId);
      // Notify NangoService about authentication (only when user ID changes)
      nangoService.onUserAuthenticated(internalUserId);
    } else if (internalUserId === backendUserId) {
      uiLogger.debug('Backend user ID unchanged, skipping NangoService notification');
    }
  }, [nangoService, backendUserId]);

  /**
   * Helper function to clear all authentication state and storage.
   * Also resets the chat store so the next user never sees previous user's data.
   */
  const clearAuthState = useCallback((includeOnboardingFlags: boolean = false): void => {
    setUser(null);
    setSession(null);
    setBackendUserId(null);
    localStorage.removeItem('auth_session');
    localStorage.removeItem('csrf_token');
    clearDatadogUser();

    if (includeOnboardingFlags) {
      sessionStorage.removeItem('new_user_signup');
    }

    // Clear chat store on any auth clear (logout, session expiry, 401)
    useChatStore.getState().reset();
  }, []);

  // Add refresh state tracking to prevent duplicate calls
  const [refreshInProgress, setRefreshInProgress] = useState(false);

  /**
   * Handles fallback auth flow when parallel flow fails or returns no auth
   */
  const handleFallbackAuth = useCallback(async (): Promise<boolean> => {
    try {
      const userInfo = await authService.getCurrentUser();

      if (userInfo && userInfo.user && userInfo.session) {
        uiLogger.debug('Successfully restored session via fallback');
        setUser(userInfo.user);
        setSession(userInfo.session);
        updateBackendUserId();

        setAuthReady(true);
        return true;
      } else {
        uiLogger.debug('No valid session found during refresh');
        clearAuthState(true);
        setAuthReady(true);
        return false;
      }
    } catch (error) {
      uiLogger.warn('Fallback auth flow failed', { error });
      clearAuthState(true);
      setAuthReady(true);
      return false;
    }
  }, [updateBackendUserId, clearAuthState]);

  /**
   * Logs out the user by clearing all auth state
   */
  const logout = useCallback(async () => {
    // Don't allow logout during email verification - user needs to complete verification first
    // UNLESS user explicitly clicked "back to login" (logging_out flag is set)
    const currentPath = window.location.pathname;
    const isLoggingOut = sessionStorage.getItem('logging_out') === 'true';

    if (shouldSuppressRedirect(currentPath) && !isLoggingOut) {
      uiLogger.debug('Logout blocked during email verification');
      return;
    }

    try {
      await authService.logout();
      // Clear state and cleanup after successful logout
      clearAuthState();
      setAuthReady(true); // Auth state is ready (logged out)

      // Clear cached onboarding data
      navigationService.clearOnboardingCache();

      // Clear profile data cache from localStorage
      localStorage.removeItem('profile_data_cache');

      // Notify NangoService about logout
      nangoService.notifyAuthStateChange(null);
    } catch (error) {
      uiLogger.error('Logout error', { error });
      // Clear state and cleanup even if logout fails
      clearAuthState();
      setAuthReady(true); // Auth state is ready (logged out)

      // Clear cached onboarding data
      navigationService.clearOnboardingCache();

      // Clear profile data cache from localStorage
      localStorage.removeItem('profile_data_cache');

      // Notify NangoService about logout
      nangoService.notifyAuthStateChange(null);
    } finally {
      // Cancel any in-flight requests so their responses don't write stale data
      // after the next user logs in (prevents non-org cache leaking to org member)
      queryClient.cancelQueries();
      // Clear React Query cache to remove any persisted organization/user data
      queryClient.removeQueries();

      analyticsEvents.session.logout();
      // Clear user ID on logout
      await setAnalyticsUserId(null);
    }
  }, [clearAuthState, nangoService, queryClient]);

  /**
   * Handles authentication errors
   */
  const handleAuthError = useCallback(
    async (error: unknown) => {
      const currentPath = window.location.pathname;

      // 1. Skip auth error handling during sensitive flows (like email verification)
      if (shouldSuppressRedirect(currentPath)) {
        uiLogger.debug('Skipping auth error handling during email verification');
        setAuthReady(true);
        return;
      }

      uiLogger.error('Error during auth refresh', { error });

      // 2. Delegate standardized auth error handling (401/403) to shared helper
      const isHandled = handleSharedAuthError({
        error,
        context: 'auth refresh',
        shouldSuppressRedirect: true, // We handle cleanup manually if needed via callbacks
        onUnauthorized: () => {
          clearAuthState(true);
        },
        onForbidden: () => {
          // 403s are logged by the helper but don't necessarily require clearing state
          setAuthReady(true);
        },
      });

      // 3. If the error was handled as a specific auth failure, we're done
      if (isHandled) {
        setAuthReady(true);
        return;
      }

      // 4. Default behavior for non-response errors (e.g. network)
      // We keep the existing state to allow for potential retry
      uiLogger.warn('Non-auth error during auth refresh, keeping existing state');
      setAuthReady(true);
    },
    [clearAuthState],
  );

  /**
   * Performs the actual auth refresh using parallel or fallback flow
   */
  const performAuthRefresh = useCallback(async () => {
    try {
      // Constants for auth refresh logic
      const RECENTLY_AUTHENTICATED_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes

      // Check if we recently authenticated (within last 3 minutes)
      const now = Date.now();
      const recentlyAuthenticated =
        authService.isRecentlyAuthenticated &&
        now - authService.lastAuthTime < RECENTLY_AUTHENTICATED_THRESHOLD_MS;

      // Additional check: if we just completed login (sessionStorage flag)
      const justLoggedIn = sessionStorage.getItem('just_logged_in') === 'true';

      if (recentlyAuthenticated || justLoggedIn) {
        // Clear the flag after using it
        sessionStorage.removeItem('just_logged_in');
        uiLogger.debug('Skipping auth refresh - recently authenticated, using cached data');
      }

      // Check if auth service has valid cached data
      const cachedUserInfo = await authService.getCurrentUser().catch(() => null);
      if (cachedUserInfo?.user) {
        uiLogger.debug('Using cached auth data from auth service');
        setUser(cachedUserInfo.user);
        setSession(cachedUserInfo.session);
        updateBackendUserId();

        setAuthReady(true);
        return;
      }

      // Fall back to standard auth flow
      await handleFallbackAuth();
    } catch (error) {
      uiLogger.warn('Auth flow failed, falling back to traditional flow', {
        error,
      });
      await handleFallbackAuth();
    }
  }, [handleFallbackAuth, updateBackendUserId]);

  /**
   * Refreshes the authentication state without triggering redirects
   * Used internally and exposed to consumers of the context
   */
  const refreshAuth = useCallback(async () => {
    if (refreshInProgress) {
      uiLogger.debug('Auth refresh already in progress, skipping');
      return;
    }

    try {
      setRefreshInProgress(true);
      setLoading(true); // Ensure loading is set to true during refresh
      setAuthReady(false);

      await performAuthRefresh();
      setAuthReady(true); // Auth refresh completed successfully
    } catch (error) {
      await handleAuthError(error);
    } finally {
      setLoading(false);
      setRefreshInProgress(false);
    }
  }, [refreshInProgress, handleAuthError, performAuthRefresh]);

  /**
   * Registers a new user account
   * @param email - User's email address
   * @param password - User's password
   * @param verificationCode - Optional verification code for registration
   * @param firstName - Optional first name
   * @param lastName - Optional last name
   * @param invitationToken - Optional invitation token
   * @param termsAccepted - Whether user accepted terms/privacy (sent to backend for compliance)
   * @param marketingEmailsOptIn - Optional consent for tips/marketing email
   */
  const register = async (
    email: string,
    password?: string,
    verificationCode?: string,
    firstName?: string,
    lastName?: string,
    invitationToken?: string,
    termsAccepted?: boolean,
    marketingEmailsOptIn?: boolean,
  ) => {
    try {
      await authService.register(
        email,
        password,
        verificationCode,
        firstName,
        lastName,
        invitationToken,
        termsAccepted,
        marketingEmailsOptIn,
      );
      // Set a flag to indicate we're in the signup flow
      // This prevents ProtectedRoute and navigationService from redirecting authenticated users away from /verify-email
      sessionStorage.setItem('signup_in_progress', 'true');

      // Track signup event in Firebase Analytics (fire-and-forget)
      analyticsEvents.auth.signUp({
        method: password ? 'email' : 'invitation',
      });

      // Don't await refreshAuth() - let it complete in the background
      // This prevents race condition where refreshAuth() completes and triggers redirects
      // before CreateAccount can navigate to /verify-email
      // The navigation to /verify-email should happen immediately after this function returns
      refreshAuth().catch((error) => {
        uiLogger.warn(
          'Auth refresh failed during signup, but continuing with email verification flow',
          {
            error,
          },
        );
      });
    } catch (error) {
      const appError = logError(error, 'register');
      throw appError;
    }
  };

  /**
   * Helper function to handle common post-login logic
   * Consolidates duplicated code across login methods
   */
  const handleSuccessfulLogin = async (
    userInfo: { user: any; session: any },
    loginMethod: 'email' | 'email_mfa' | 'google',
  ) => {
    // Update state immediately with the returned user info
    setUser(userInfo.user);
    setSession(userInfo.session);
    setLoading(false);
    setAuthReady(true); // Auth complete and successful

    // Update backend user ID from authService's internal state
    updateBackendUserId();

    // Suppress analytics for excluded internal domains (e.g. @karbondigital.com)
    const email: string | undefined = userInfo?.user?.email;
    suppressAnalyticsForEmail(email);

    // Track login event and set user properties in Firebase Analytics (fire-and-forget)
    // Note: user_id is intentionally omitted from event params for privacy compliance
    // Using Firebase Analytics' setUserId() and setUserProperties() for proper user tracking
    analyticsEvents.auth.login({
      method: loginMethod,
    });

    // Set user ID for user-level analytics (Firebase handles privacy)
    // Use backend user ID from authService for consistency
    const backendUserId = authService.getBackendUserId();
    if (backendUserId) {
      try {
        setDatadogUser({
          id: backendUserId,
          email,
          name: null,
          organizationId: null,
        });
        // Await analytics calls to prevent race conditions where user properties
        // are set before user ID
        await setAnalyticsUserId(backendUserId);

        // Set user properties for segmentation
        await setAnalyticsUserProperties({
          login_method: loginMethod,
          is_internal_user: isEmailExcludedFromAnalytics(email),
          email_domain: email?.includes('@') ? email.split('@').pop()?.toLowerCase() || null : null,
          environment: ENV.VITE_ENV,
          app_version: ENV.VITE_APP_VERSION || null,
          auth_provider: ENV.VITE_AUTH_PROVIDER || null,
          // Add more user properties as needed (e.g., plan, role, etc.)
        });
      } catch (error) {
        uiLogger.error('Failed to set analytics user ID or properties', { error, backendUserId });
      }
    } else {
      // Backend user ID not available - log warning for debugging
      uiLogger.warn('Backend user ID not available for analytics tracking', {
        loginMethod,
        userExists: !!userInfo.user,
      });

      // Still track login method for analytics even without user ID
      try {
        await setAnalyticsUserProperties({
          login_method: loginMethod,
          is_internal_user: isEmailExcludedFromAnalytics(email),
          email_domain: email?.includes('@') ? email.split('@').pop()?.toLowerCase() || null : null,
          environment: ENV.VITE_ENV,
          app_version: ENV.VITE_APP_VERSION || null,
          auth_provider: ENV.VITE_AUTH_PROVIDER || null,
        });
      } catch (error) {
        uiLogger.warn('Failed to set analytics user properties (user ID unavailable)', { error });
      }
    }

    await navigationService.redirectAfterLogin();
  };

  /**
   * Authenticates a user with email and password
   * @param email - User's email address
   * @param password - User's password
   */
  const login = async (email: string, password: string) => {
    try {
      setAuthReady(false); // Auth in progress

      const userInfo = await authService.login(email, password);
      await handleSuccessfulLogin(userInfo, 'email');
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed
      // Preserve MFA error properties - don't transform them
      if (error && typeof error === 'object' && 'requires_mfa' in error) {
        throw error;
      }
      const appError = logError(error, 'login');
      throw appError;
    }
  };

  /**
   * Verify MFA code and complete login
   * @param mfaToken - Temporary MFA token from login response
   * @param code - MFA verification code
   * @param backupCode - Optional backup code
   */
  const verifyMFAAndLogin = async (mfaToken: string, code: string, backupCode?: string) => {
    try {
      setAuthReady(false); // Auth in progress

      // Track MFA verification started
      analyticsEvents.auth.mfaVerificationStarted();

      const userInfo = await authService.verifyMFALogin(mfaToken, code, backupCode);

      // Track MFA verification completed
      analyticsEvents.auth.mfaVerificationCompleted();

      await handleSuccessfulLogin(userInfo, 'email_mfa');
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed

      // Track MFA verification failed
      analyticsEvents.auth.mfaVerificationFailed({
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      const appError = logError(error, 'mfa_verify');
      throw appError;
    }
  };

  /**
   * Authenticates a user with Google OAuth
   * @param verificationCode - Optional verification code for OAuth authentication
   */
  const loginWithGoogle = async (verificationCode?: string) => {
    try {
      setAuthReady(false); // Auth in progress

      // Track Google login started
      analyticsEvents.auth.googleLoginStarted();

      const userInfo = await authService.loginWithGoogle(verificationCode);
      await handleSuccessfulLogin(userInfo, 'google');
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed
      // Log the error but don't transform it - let the caller check for verification code requirements
      uiLogger.error('Google sign-in error in AuthContext', { error });
      throw error;
    }
  };

  /**
   * Authenticates a user with Microsoft OAuth
   * @param verificationCode - Optional verification code for OAuth authentication
   */
  const loginWithMicrosoft = async (verificationCode?: string) => {
    try {
      setAuthReady(false); // Auth in progress

      const userInfo = await authService.loginWithMicrosoft(verificationCode);

      // Update state immediately with the returned user info
      setUser(userInfo.user);
      setSession(userInfo.session);
      setLoading(false);
      setAuthReady(true); // Auth complete and successful

      // Update backend user ID from authService's internal state
      updateBackendUserId();

      await navigationService.redirectAfterLogin();
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed
      // Log the error but don't transform it - let the caller check for verification code requirements
      uiLogger.error('Microsoft sign-in error in AuthContext', { error });
      throw error;
    }
  };

  /**
   * Authenticates a user with Okta SSO
   */
  const loginWithOkta = async () => {
    try {
      setAuthReady(false); // Auth in progress
      setLoading(true);

      // Track Okta login started
      analyticsEvents.auth.oktaLoginStarted();

      await authService.loginWithOkta();
      // Note: loginWithOkta redirects to Okta, so we won't reach here
      // The callback handler will complete the authentication
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed
      uiLogger.error('Okta sign-in error in AuthContext', { error });
      throw error;
    }
  };

  /**
   * Authenticates a user with company SSO (Okta or Microsoft)
   * Automatically detects provider based on company identifier
   * @param companyIdentifier - Company identifier (e.g., domain or custom identifier)
   */
  const loginWithCompanySSO = async (companyIdentifier: string) => {
    try {
      setAuthReady(false); // Auth in progress
      setLoading(true);

      // Delegate to centralized service method which handles lookup, validation, and redirection
      await authService.loginWithCompanySSO(companyIdentifier);

      // Note: Redirects will happen, so we won't reach here normally
      // The callback handlers will complete the authentication
    } catch (error) {
      setLoading(false);
      setAuthReady(true); // Auth complete but failed
      uiLogger.error('Company SSO login error in AuthContext', { error });
      throw error;
    }
  };

  const checkOnboardingStatus = async (): Promise<OnboardingStatus | null> => {
    if (!user) {
      return null;
    }

    try {
      // Use backend user ID if available, otherwise use Supabase user ID
      const userId = backendUserId || user.id;
      return await onboardingService.checkOnboardingStatus(userId);
    } catch (error) {
      uiLogger.error('Error checking onboarding status', { error });
      return null;
    }
  };

  /**
   * Get the backend user ID for integrations
   * This is the ID that should be used for all backend API calls
   */
  const getBackendUserId = useCallback((): string | null => {
    return backendUserId;
  }, [backendUserId]);

  const hasSessionExpired = useCallback((): boolean => {
    if (!session) return true;

    const currentTime = Date.now() / 1000;
    return !!(session.expires_at && session.expires_at < currentTime);
  }, [session]);

  // Initialize authentication on mount (runs only once)
  useEffect(() => {
    let mounted = true;
    let initializationComplete = false;

    const initializeAuth = async () => {
      if (mounted && !refreshInProgress && !initializationComplete) {
        // Skip auth initialization on callback routes - let them handle auth themselves
        const currentPath = window.location.pathname;
        if (
          currentPath.startsWith('/auth/okta/callback') ||
          currentPath.startsWith('/auth/microsoft/callback')
        ) {
          uiLogger.debug('Skipping auth initialization for SSO callback route', { currentPath });
          initializationComplete = true;
          return;
        }

        initializationComplete = true;
        await refreshAuth();
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Monitor session validity and set up periodic checks
  useEffect(() => {
    let sessionCheckTimer: NodeJS.Timeout;

    const checkSessionValidity = () => {
      // Don't clear auth state if user is on email verification page or during signup
      // They need to complete email verification first
      const currentPath = window.location.pathname;

      if (shouldSuppressRedirect(currentPath)) {
        uiLogger.debug('Skipping session expiration check during email verification');
        return;
      }

      if (session && hasSessionExpired()) {
        uiLogger.info('Session expired, clearing auth state');
        clearAuthState();
      }
    };

    if (session) {
      sessionCheckTimer = setInterval(checkSessionValidity, SESSION_CHECK_INTERVAL);
    }

    return () => {
      if (sessionCheckTimer) {
        clearInterval(sessionCheckTimer);
      }
    };
  }, [session, hasSessionExpired, clearAuthState, refreshAuth, refreshInProgress]);

  // Register auth error callback to handle 401s from apiClient
  useEffect(() => {
    // Register the callback
    authService.setAuthErrorCallback((path) => {
      uiLogger.info('Auth error callback triggered', { path });
      // Refresh failure should fail closed to logged-out state.
      clearAuthState(true);
      setAuthReady(true);
    });

    // Cleanup
    return () => {
      // We don't have a way to unregister, but subsequent mounts will overwrite it
      // which is acceptable for a singleton service
    };
  }, [clearAuthState]);

  const contextValue: AuthContextType = {
    user,
    session,
    backendUserId,
    loading,
    authReady,
    login,
    verifyMFAAndLogin,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithOkta,
    loginWithCompanySSO,
    register,
    logout,
    refreshAuth,
    checkOnboardingStatus,
    isAuthenticated: !loading && !!user && !!session && !hasSessionExpired(),
    getBackendUserId,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
